/**
 * Non-hook logger factory (logging-strategy POLICY.md §1's second bullet:
 * "Non-hook client contexts — utils, effects, module-level helpers").
 *
 * Unlike useLogger.ts, there is no React context to read `sessionId` from
 * here, so this factory calls sessionId.ts's `getSessionId()` directly by
 * default — it reads the same persisted, per-browser id LoggerProvider already
 * minted, since both go through the same localStorage key.
 *
 * There is no memoization here on purpose, and none is needed: the id this
 * resolves to is stable for the page load even with no localStorage to persist
 * it in (see `mintedForThisLoad` in sessionId.ts). Every logger this factory
 * hands out therefore agrees with LoggerProvider's, which matters because
 * `makeContextProvider` freezes the id onto every event its logger emits.
 *
 * src/registerServiceWorker.ts is the in-template example of the pattern.
 */
import type { Logger } from "@bindery/logging";
import { createLogger, makeContextProvider } from "./backend";
import { getSessionId } from "./sessionId";

export interface ClientLoggerContext {
  /** Your auth provider's opaque subject, to include on every event if known. */
  userId?: string;
  /** Override the persisted sessionId — mainly useful for tests. */
  sessionId?: string;
  /**
   * The marketing lead this browser arrived as.
   *
   * Supplied explicitly here because the one caller that needs it —
   * `recordLanding` in ../lib/attribution.ts — emits `acquisition.landed`
   * while resolving the lead, which is strictly before LoggerProvider has a
   * context to read it from. Every other caller gets it from the provider via
   * `useLogger`.
   */
  leadId?: string;
}

export function createClientLogger(context?: ClientLoggerContext): Logger {
  const sessionId = context?.sessionId ?? getSessionId();
  const userId = context?.userId;

  return createLogger(makeContextProvider(sessionId, userId, context?.leadId));
}
